import { useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/shared/lib/supabase'

interface UpdateProfileData {
  full_name: string
  avatar_url?: string
}

const AVATAR_BUCKET = 'avatars'
const MAX_AVATAR_SIZE = 2 * 1024 * 1024 // 2MB
const ALLOWED_AVATAR_TYPES = ['image/jpeg', 'image/png', 'image/webp']

export function validateAvatarFile(file: File): { valid: boolean; error?: string } {
  if (!ALLOWED_AVATAR_TYPES.includes(file.type)) {
    return { valid: false, error: 'fileTypeNotAllowed' }
  }
  if (file.size > MAX_AVATAR_SIZE) {
    return { valid: false, error: 'fileTooLarge' }
  }
  return { valid: true }
}

async function uploadAvatar(userId: string, file: File): Promise<string> {
  const fileExt = file.name.split('.').pop()?.toLowerCase() || 'jpg'
  const filePath = `${userId}/avatar.${fileExt}`

  // Delete existing avatar if any
  await supabase.storage.from(AVATAR_BUCKET).remove([filePath])

  // Upload new avatar
  const { error: uploadError } = await supabase.storage
    .from(AVATAR_BUCKET)
    .upload(filePath, file, {
      cacheControl: '3600',
      upsert: true,
    })

  if (uploadError) {
    throw new Error(uploadError.message)
  }

  // Get public URL
  const { data } = supabase.storage.from(AVATAR_BUCKET).getPublicUrl(filePath)

  // Add timestamp to bust cache
  return `${data.publicUrl}?t=${Date.now()}`
}

async function updateProfile(
  userId: string,
  data: UpdateProfileData,
  avatarFile?: File
): Promise<void> {
  let avatarUrl = data.avatar_url

  // Upload new avatar if provided
  if (avatarFile) {
    avatarUrl = await uploadAvatar(userId, avatarFile)
  }

  // Update user profile
  const { error } = await supabase
    .from('users')
    .update({
      full_name: data.full_name,
      avatar_url: avatarUrl,
      updated_at: new Date().toISOString(),
    })
    .eq('id', userId)

  if (error) {
    throw new Error(error.message)
  }
}

export function useUpdateProfile(userId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ data, avatarFile }: { data: UpdateProfileData; avatarFile?: File }) =>
      updateProfile(userId, data, avatarFile),
    onSuccess: () => {
      // Invalidate user-related queries
      queryClient.invalidateQueries({ queryKey: ['currentUser'] })
      queryClient.invalidateQueries({ queryKey: ['user'] })
    },
  })
}

export { MAX_AVATAR_SIZE, ALLOWED_AVATAR_TYPES }
